import React, { useState } from 'react'

import { encodeToBase64, isACorrectFormat } from '../../../core/utils'
import { useAjaxContext } from '../../../core/ajax'
import { DEFAULT_FILE_SIZE, API_ENDPOINT } from '../constants'

/**
 * @typedef {object} FileUploaderProps
 * @property {object} classes - Clases CSS (no utilizado directamente).
 * @property {Array<string>} accept - Array de tipos MIME aceptados.
 * @property {function(object):void} onSuccess - Callback invocado al subir el archivo exitosamente.
 * @property {function(object):void} onError - Callback invocado al encontrar un error.
 * @property {boolean} disabled - Si es true, deshabilita el input de archivo.
 * @property {number} [maxSize] - Tamaño máximo del archivo en bytes.
 * @property {string} [type='sin_informar'] - Tipo de documento.
 * @property {string|number} applicationId - ID de la solicitud asociada.
 * @property {string|number} [budgetId] - ID del presupuesto asociado (opcional).
 * @property {function({loading: boolean, disabled: boolean}):React.ReactNode} children - Función render prop para el contenido del label.
 * @property {string} id - Identificador único para el input.
 * @property {number|string} clientId - ID del cliente.
 */

/**
 * Componente para subir archivos y enviarlos al backend (asociados a solicitudes/presupuestos).
 *
 * Maneja la selección de archivos, validación de formato y tamaño, codificación a base64,
 * y envío al endpoint del backend con información de solicitud/presupuesto. Utiliza render props
 * para delegar la UI del botón/elemento de carga al componente padre.
 *
 * @component
 * @example
 * <FileUploader
 *   id="doc-upload"
 *   accept={['application/pdf', 'image/jpeg']}
 *   maxSize={5242880}
 *   type="contrato"
 *   applicationId={123}
 *   clientId={456}
 *   onSuccess={(data) => console.log('Subido:', data)}
 *   onError={(err) => console.error('Error:', err)}
 * >
 *   {({ loading, disabled }) => (
 *     <ThemeButton loading={loading} disabled={disabled}>
 *       Subir Archivo
 *     </ThemeButton>
 *   )}
 * </FileUploader>
 *
 * @param {FileUploaderProps} props - Las props del componente FileUploader.
 */
export const FileUploader = ({
  classes,
  accept,
  onSuccess,
  onError,
  disabled,
  maxSize = DEFAULT_FILE_SIZE,
  type = 'sin_informar',
  applicationId,
  budgetId,
  children,
  ...props
}) => {
  const { post } = useAjaxContext()
  const [loading, setLoading] = useState(false)

  const handleChangeUpload = async (e) => {
    setLoading(true)
    const file = e.target.files[0]
    if (file) {
      if (accept && accept.length > 0 && !isACorrectFormat(file.name, accept)) {
        setLoading(false)
        return onError({ mssg: 'Archivo con formato no válido' })
      } else if (file.size > maxSize) {
        setLoading(false)
        return onError({ mssg: 'Excede el tamaño máximo' })
      }
      const mockErrorData = {
        mssg: 'Problema con la subida del fichero',
      }
      postDocument(file)
        .then(({ data }) => {
          setLoading(false)
          return onSuccess(data)
        })
        .catch(({ response }) => {
          setLoading(false)
          return onError(response ? response.data : mockErrorData)
        })
    }
  }

  const postDocument = async (file) => {
    const base64File = await encodeToBase64(file)
    const payload = {
      documento: base64File.split(',')[1],
      nombre: file.name.replace(/\.[^/.]+$/, ''),
      tipo: type,
      extension: file.type.split('/')[1],
      cliente_id: parseInt(props.clientId),
      solicitud_id: applicationId.toString(),
    }
    if (budgetId) {
      payload['presupuesto_id'] = budgetId.toString()
    }
    return post(API_ENDPOINT, payload, { timeout: 0 })
  }

  return (
    <div>
      <input
        id={`file-upload-${props.id}`}
        type="file"
        style={{ display: 'none' }}
        accept={accept.join(',')}
        onChange={handleChangeUpload}
        disabled={disabled}
      />
      <label htmlFor={`file-upload-${props.id}`}>{children({ loading, disabled })}</label>
    </div>
  )
}
